/**
 * useOrganizationTree
 * Hämtar hela org-trädet för inloggad användare via GET /api/organizations/tree (ett anrop).
 * Använder React Query för cache, deduplicering och staleTime (5 min).
 * Används av OrgTreeSelect och kan användas av rapporter för att slippa N+1.
 */

import { useQuery } from 'react-query';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api/client';

export interface OrgTreeNode {
  id: string;
  name: string;
  children?: OrgTreeNode[];
}

export interface UseOrganizationTreeResult {
  tree: OrgTreeNode[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

/** Längre staleTime minskar belastning: trädet ändras sällan jämfört med fordonslistan. */
const TREE_STALE_MS = 15 * 60 * 1000; // 15 min

function sortTreeSwedish(nodes: OrgTreeNode[]): OrgTreeNode[] {
  const sorted = [...nodes].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  for (const node of sorted) {
    if (node.children?.length) {
      node.children = sortTreeSwedish(node.children);
    }
  }
  return sorted;
}

export function useOrganizationTree(): UseOrganizationTreeResult {
  const { user } = useAuthStore();

  const {
    data: tree = [],
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery(
    ['organizationTree'],
    async () => {
      const response = await api.get('/api/organizations/tree');
      if (response.data?.success && Array.isArray(response.data.data)) {
        return sortTreeSwedish(response.data.data as OrgTreeNode[]);
      }
      return [];
    },
    {
      enabled: !!user,
      staleTime: TREE_STALE_MS,
      retry: 1,
    }
  );

  const error = queryError
    ? (queryError instanceof Error ? queryError : new Error('Failed to load organization tree'))
    : null;

  return { tree, loading, error, refetch };
}
